import './automations.scss';
import React from 'react';
import { Button } from '../Button/Button';
import { Tooltip } from '../Tooltip/Tooltip';
import { Text } from '../text/text';
import { t } from '../../locales/translate';
import { useModalPresenter } from '../Modal/PresentedModal';
import { Icon } from '../Icon/Icon';
import { formatDateTime } from '../../util/formatters';
import { AutomationForm } from './form';
import classnames from 'classnames';

export type AutomatableType = 'VOUCHER_CAMPAIGN';

interface Props {
  automatableId: string;
  automatableType: AutomatableType;
  automations?: Array<{
    id: string;
    operation: string;
    executeAt: string;
  }>;
  running: boolean;
  permissions: {
    start?: boolean;
    stop?: boolean;
  };
  onStart: () => void;
  onStop: () => void;
}

export function Automations({
  automatableId,
  automatableType,
  automations = [],
  permissions,
  running,
  onStart,
  onStop,
}: Props): JSX.Element {
  async function startNow(): Promise<void> {
    onStart?.();
  }
  async function stopNow(): Promise<void> {
    onStop?.();
  }

  const modal = useModalPresenter();

  let referenceButton = <Button icon="play" type="automation-play" disabled={!permissions?.start} />;
  let instantAction = (
    <li className="automation__item" onClick={startNow}>
      <Text type="automation-menu-item">{t.model.automation.startNow}</Text>
      <div className="automation__icon">
        <Icon name="play" />
      </div>
    </li>
  );

  if (running) {
    referenceButton = <Button icon="pause" type="automation-pause" disabled={!permissions?.stop} />;
    instantAction = (
      <li className="automation__item" onClick={stopNow}>
        <Text type="automation-menu-item">{t.model.automation.stopNow}</Text>
        <div className="automation__icon">
          <Icon name="pause" />
        </div>
      </li>
    );
  }

  const startAutomation = automations.find((a) => a.operation === 'START');
  const endAutomation = automations.find((a) => a.operation === 'STOP');

  const nextAutomation = running ? endAutomation : startAutomation;

  if (nextAutomation) {
    referenceButton = (
      <button className="automation__scheduled">
        <Text type="automation-scheduled">{t.model.automation.operation[nextAutomation.operation.toLowerCase()]}:</Text>
        <Text type="automation-scheduled" bold>
          {formatDateTime(nextAutomation.executeAt)}
        </Text>
        <div className="automation__scheduled-icon">
          <Icon name="calendar" />
        </div>
      </button>
    );
  }

  return (
    <Tooltip
      mode="click"
      reference={referenceButton}
      closeOnChildrenClick
      position="bottom-end"
      className="automation__tooltip"
      disabled={!running ? !permissions?.start : !permissions?.stop}
    >
      <ul className="automation__menu">
        {instantAction}
        <li
          className="automation__item"
          onClick={() => {
            modal.present(AutomationForm, { automatableId, automatableType }, { size: 'xs' });
          }}
        >
          <Text type="automation-menu-item">{t.model.automation.automation}</Text>
          {automations.length > 0 ? (
            <div className="automation__icon">
              <Icon name="edit" />
            </div>
          ) : (
            <div className={classnames('automation__icon')}>
              <Icon name="plus-circle" />
            </div>
          )}
        </li>
      </ul>
    </Tooltip>
  );
}
